"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WorkbookCells = exports.partText = exports.MissingFromWorkbook = void 0;
exports.sheetParts = sheetParts;
exports.sharedStrings = sharedStrings;
exports.sheetValues = sheetValues;
exports.shiftFormula = shiftFormula;
exports.patchSheet = patchSheet;
exports.writeCells = writeCells;
exports.recalculateOnOpen = recalculateOnOpen;
const decoder = new TextDecoder();
const encoder = new TextEncoder();
/** A sheet or part the workbook should have isn't there. */
class MissingFromWorkbook extends Error {
}
exports.MissingFromWorkbook = MissingFromWorkbook;
const partText = (parts, path) => {
    const bytes = parts[path];
    if (!bytes)
        throw new MissingFromWorkbook(`The workbook has no ${path}.`);
    return decoder.decode(bytes);
};
exports.partText = partText;
const setPart = (parts, path, text) => {
    parts[path] = encoder.encode(text);
};
const ENTITIES = { lt: '<', gt: '>', amp: '&', quot: '"', apos: "'" };
const unescapeXml = (text) => text.replace(/&(#x[0-9a-f]+|#\d+|\w+);/gi, (whole, name) => {
    if (name[0] === '#')
        return String.fromCodePoint(name[1].toLowerCase() === 'x' ? parseInt(name.slice(2), 16) : Number(name.slice(1)));
    return ENTITIES[name] ?? whole;
});
const escapeXml = (text) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const attr = (attrs, name) => attrs.match(new RegExp(`\\b${name}="([^"]*)"`))?.[1] ?? null;
// ---------- where the sheets are ----------
/** Each sheet's part path by its tab name ("Take Off" → "xl/worksheets/sheet1.xml"). */
function sheetParts(parts) {
    const rels = (0, exports.partText)(parts, 'xl/_rels/workbook.xml.rels');
    const targets = new Map([...rels.matchAll(/<Relationship\b([^>]*)\/?>/g)].map((m) => [attr(m[1], 'Id'), attr(m[1], 'Target') ?? '']));
    const out = {};
    for (const m of (0, exports.partText)(parts, 'xl/workbook.xml').matchAll(/<sheet\b([^>]*)\/>/g)) {
        const target = targets.get(attr(m[1], 'r:id'));
        if (target)
            out[unescapeXml(attr(m[1], 'name') ?? '')] = target.startsWith('/') ? target.slice(1) : `xl/${target}`;
    }
    return out;
}
// ---------- reading cells ----------
/** The shared strings, in order: rich text runs joined, phonetic hints left out. */
function sharedStrings(parts) {
    if (!parts['xl/sharedStrings.xml'])
        return [];
    return [...(0, exports.partText)(parts, 'xl/sharedStrings.xml').matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => [...m[1].replace(/<rPh\b[\s\S]*?<\/rPh>/g, '').matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((t) => unescapeXml(t[1])).join(''));
}
const CELL = /<c r="([A-Z]+\d+)"([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g;
/** A cell's cached value as Excel last saved it: a formula's result, text, a number; an error or blank as null. */
function cachedValue(attrs, inner, strings) {
    const type = attr(attrs, 't');
    if (type === 'inlineStr')
        return unescapeXml([...inner.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((t) => t[1]).join(''));
    const raw = inner.match(/<v>([\s\S]*?)<\/v>/)?.[1];
    if (raw === undefined || type === 'e')
        return null;
    if (type === 's')
        return strings[Number(raw)] ?? null;
    if (type === 'str')
        return unescapeXml(raw);
    if (type === 'b')
        return raw === '1';
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}
/** Every cell of one sheet that holds a value, by address. */
function sheetValues(sheetXml, strings) {
    const values = new Map();
    for (const m of sheetXml.matchAll(CELL)) {
        const value = cachedValue(m[2], m[3] ?? '', strings);
        if (value !== null && value !== '')
            values.set(m[1], value);
    }
    return values;
}
/** A workbook's cached cell values, sheet by sheet as they're asked for. */
class WorkbookCells {
    parts;
    paths;
    strings;
    sheets = new Map();
    constructor(parts) {
        this.parts = parts;
        this.paths = sheetParts(parts);
        this.strings = sharedStrings(parts);
    }
    sheet(name) {
        let values = this.sheets.get(name);
        if (!values) {
            const path = this.paths[name];
            if (!path)
                throw new MissingFromWorkbook(`The workbook has no "${name}" sheet.`);
            values = sheetValues((0, exports.partText)(this.parts, path), this.strings);
            this.sheets.set(name, values);
        }
        return values;
    }
    value(sheet, address) {
        return this.sheet(sheet).get(address) ?? null;
    }
    text(sheet, address) {
        const v = this.value(sheet, address);
        return v === null || typeof v === 'boolean' ? '' : String(v).trim();
    }
    /** A number, or null when blank, text or an error. */
    num(sheet, address) {
        const v = this.value(sheet, address);
        return typeof v === 'number' ? v : null;
    }
    /** The last row holding anything. */
    lastRow(sheet) {
        let last = 0;
        for (const address of this.sheet(sheet).keys())
            last = Math.max(last, Number(address.replace(/^[A-Z]+/, '')));
        return last;
    }
}
exports.WorkbookCells = WorkbookCells;
// ---------- writing cells ----------
const colNumber = (letters) => [...letters].reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0);
const colLetters = (n) => (n <= 0 ? '' : colLetters(Math.floor((n - 1) / 26)) + String.fromCharCode(65 + ((n - 1) % 26)));
const splitAddress = (address) => {
    const m = address.match(/^([A-Z]+)(\d+)$/);
    if (!m)
        throw new Error(`Not a cell address: ${address}`);
    return { col: colNumber(m[1]), row: Number(m[2]) };
};
/**
 * A formula moved by (rows, cols): relative references move, $-anchored parts
 * don't, text in quotes is left alone. Whole-row or whole-column ranges are
 * refused rather than guessed.
 */
function shiftFormula(formula, rows, cols) {
    return formula
        .split(/("(?:[^"]|"")*")/)
        .map((part, i) => {
        if (i % 2)
            return part;
        if (/(^|[^A-Za-z0-9_$])\$?[A-Z]{1,3}:\$?[A-Z]{1,3}(?![A-Za-z0-9(])|(^|[^A-Za-z0-9_$.])\$?\d+:\$?\d+(?![0-9.])/.test(part)) {
            throw new Error(`Can't move a formula with a whole-row or whole-column range: ${formula}`);
        }
        return part.replace(/(^|[^A-Za-z0-9_$.])(\$?)([A-Z]{1,3})(\$?)(\d+)(?![A-Za-z0-9_(])/g, (_m, lead, colAbs, col, rowAbs, row) => {
            const c = colAbs ? col : colLetters(colNumber(col) + cols);
            const r = rowAbs ? row : String(Number(row) + rows);
            return `${lead}${colAbs}${c}${rowAbs}${r}`;
        });
    })
        .join('');
}
function cellXml(address, attrs, value) {
    // Keep the cell's style; its old type, metadata and formula go with its old value.
    const keep = attrs.replace(/\s(t|cm|vm|ph)="[^"]*"/g, '');
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            throw new Error(`${address}: ${value} is not a number Excel can hold.`);
        return `<c r="${address}"${keep}><v>${value}</v></c>`;
    }
    if (typeof value === 'string')
        return `<c r="${address}"${keep} t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
    return `<c r="${address}"${keep}><f>${escapeXml(value.formula)}</f></c>`;
}
/**
 * A shared formula's first cell holds the text every other cell in the group
 * is worked out from. Typing over that first cell would orphan the rest, so the
 * next cell of the group not being written becomes the first, with the text
 * moved to it.
 */
function promotions(sheetXml, writes) {
    const masters = [];
    const members = new Map();
    for (const m of sheetXml.matchAll(CELL)) {
        const f = (m[3] ?? '').match(/<f\b([^>]*?)(?:\/>|>([\s\S]*?)<\/f>)/);
        if (!f || attr(f[1], 't') !== 'shared')
            continue;
        const si = attr(f[1], 'si');
        const ref = attr(f[1], 'ref');
        if (ref && f[2] !== undefined)
            masters.push({ si, ref, formula: unescapeXml(f[2]), address: m[1] });
        else
            (members.get(si) ?? members.set(si, []).get(si) ?? []).push(m[1]);
    }
    const out = new Map();
    for (const master of masters.filter((x) => writes.has(x.address))) {
        const next = (members.get(master.si) ?? []).find((a) => !writes.has(a));
        if (!next)
            continue;
        const from = splitAddress(master.address);
        const to = splitAddress(next);
        const end = master.ref.split(':')[1] ?? next;
        const formula = shiftFormula(master.formula, to.row - from.row, to.col - from.col);
        out.set(next, `<f t="shared" ref="${next}:${end}" si="${master.si}">${escapeXml(formula)}</f>`);
    }
    return out;
}
const ROW = /<row r="(\d+)"([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g;
/** The sheet with `writes` (by address) in place; every other cell, and everything outside the cells, as it was. */
function patchSheet(sheetXml, writes) {
    const promote = promotions(sheetXml, writes);
    const byRow = new Map();
    for (const address of [...writes.keys(), ...promote.keys()]) {
        const { row } = splitAddress(address);
        (byRow.get(row) ?? byRow.set(row, new Set()).get(row))?.add(address);
    }
    const done = new Set();
    const out = sheetXml.replace(ROW, (whole, r, rowAttrs, inner) => {
        const row = Number(r);
        const wanted = byRow.get(row);
        if (!wanted)
            return whole;
        done.add(row);
        const seen = new Set();
        let cells = (inner ?? '').replace(CELL, (cell, address, attrs, content) => {
            if (!wanted.has(address))
                return cell;
            seen.add(address);
            const write = writes.get(address);
            if (write !== undefined)
                return cellXml(address, attrs, write);
            // A promoted cell: its old shared-formula reference becomes the group's first cell.
            return `<c r="${address}"${attrs}>${(content ?? '').replace(/<f\b[^>]*?\/>/, promote.get(address))}</c>`;
        });
        // Cells the row doesn't have yet go in column order.
        for (const address of [...wanted].filter((a) => !seen.has(a) && writes.has(a))) {
            const col = splitAddress(address).col;
            const xml = cellXml(address, '', writes.get(address));
            const after = [...cells.matchAll(CELL)].find((m) => splitAddress(m[1]).col > col);
            cells = after?.index === undefined ? cells + xml : cells.slice(0, after.index) + xml + cells.slice(after.index);
        }
        // spans is only a hint; it would be wrong once a cell is added outside it.
        return `<row r="${r}"${rowAttrs.replace(/\sspans="[^"]*"/, '')}>${cells}</row>`;
    });
    const missing = [...byRow.keys()].filter((row) => !done.has(row));
    if (missing.length)
        throw new Error(`The sheet has no row ${missing.join(', ')} to write into.`);
    return out;
}
/** Write cells into one sheet of the workbook, by tab name. */
function writeCells(parts, sheet, writes) {
    const path = sheetParts(parts)[sheet];
    if (!path)
        throw new MissingFromWorkbook(`The workbook has no "${sheet}" sheet.`);
    setPart(parts, path, patchSheet((0, exports.partText)(parts, path), writes));
}
/**
 * Make Excel work every formula out when the file opens (no stale totals on
 * screen), and drop the calculation chain — it lists formula cells, and a cell
 * that was typed over no longer is one.
 */
function recalculateOnOpen(parts) {
    const book = (0, exports.partText)(parts, 'xl/workbook.xml');
    const calc = book.match(/<calcPr\b[^>]*?\/>/)?.[0];
    if (calc) {
        const full = /fullCalcOnLoad="[^"]*"/.test(calc) ? calc.replace(/fullCalcOnLoad="[^"]*"/, 'fullCalcOnLoad="1"') : calc.replace(/\/>$/, ' fullCalcOnLoad="1"/>');
        setPart(parts, 'xl/workbook.xml', book.replace(calc, full));
    }
    else {
        setPart(parts, 'xl/workbook.xml', book.replace(/(<\/definedNames>|<\/sheets>)/, '$1<calcPr fullCalcOnLoad="1"/>'));
    }
    delete parts['xl/calcChain.xml'];
    setPart(parts, 'xl/_rels/workbook.xml.rels', (0, exports.partText)(parts, 'xl/_rels/workbook.xml.rels').replace(/<Relationship\b[^>]*Target="[^"]*calcChain\.xml"[^>]*\/>/, ''));
    setPart(parts, '[Content_Types].xml', (0, exports.partText)(parts, '[Content_Types].xml').replace(/<Override\b[^>]*PartName="\/xl\/calcChain\.xml"[^>]*\/>/, ''));
}
